import { observer } from 'mobx-react-lite';
import { GridState } from '../../state';
import { Box } from '@wix/design-system';
import { FiltersMap } from '@wix/bex-core';
import React, { cloneElement, ReactElement, useMemo } from 'react';

export interface SkeletonGridProps<T, F extends FiltersMap> {
  state: GridState<T, F>;
  skeletonRows?: number;
  renderItem: () => ReactElement;
}

function _SkeletonGrid<T, F extends FiltersMap>(
  props: SkeletonGridProps<T, F>,
) {
  const { state, renderItem, skeletonRows = 2 } = props;

  const {
    calcGridProps: { columnCount },
    itemsContentWidth,
  } = state;

  const { gridTemplateColumns, children } = useMemo(() => {
    return {
      gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
      children: new Array(columnCount * skeletonRows)
        .fill(undefined)
        .map((_, i) =>
          cloneElement(renderItem(), {
            key: i,
          }),
        ),
    };
  }, [columnCount, skeletonRows]);

  if (!itemsContentWidth) {
    return null;
  }

  return (
    <Box
      dataHook="skeleton-grid"
      width="100%"
      display="grid"
      gap="SP4"
      gridAutoRows="1fr"
      gridTemplateColumns={gridTemplateColumns}
    >
      {children}
    </Box>
  );
}

export const SkeletonGrid = observer(_SkeletonGrid);
